import type { ComplianceSubject } from '../../../compliance/compliance-api';
import {
  type ComplianceExportFormat,
  type RrcEp10Document,
} from '../../../model/rrc/rrc-ep10-waste-manifest';
import type { RrcEp10WasteManifestBuildStatus } from '../../../model/rrc/rrc-ep10-waste-manifest-build-status';
import { buildStandardHeaders, otsBaseApi } from '../../ots-api';
import { RrcEp10WasteManifest } from '../../../model/rrc/rrc-ep10-waste-manifest';
import { removeUndefined } from '../../../util/util';
import { BaseQueryApi } from '@reduxjs/toolkit/query';
import { OtsAsyncThunkConfigExtra } from '../../ots-store';

export const complianceSubjectPath = (subject: ComplianceSubject) =>
  `/compliance/rrc/ep10/${
    subject.kind === 'ticket' ? 'by-tta' : 'by-pickup'
  }/${encodeURIComponent(subject.id)}`;

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: [
    'RrcEp10WasteManifests',
    'NumberRrcEp10WasteManifests',
    'NumberVersionsRrcEp10WasteManifests',
    'TtaRrcEp10WasteManifests',
    'ObjectObjectChangeLogs',
  ],
});

export const rrcEp10WasteManifestDownloadRequest = ({
  id,
  tenantCode,
  format = 'pdf',
}: {
  id: string;
  tenantCode?: string;
  format?: ComplianceExportFormat;
}) => ({
  url: `/compliance/rrc/ep10/${encodeURIComponent(id)}/download/${format}`,
  params: { tenantCode },
  responseHandler: 'text' as const,
});

export const rrcEp10WasteManifestApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRrcEp10WasteManifestBuildStatus: builder.query<
      RrcEp10WasteManifestBuildStatus | null,
      ComplianceSubject
    >({
      async queryFn(subject, api, options, query) {
        const result = await query({
          url: `${complianceSubjectPath(subject)}/status`,
          params: { tenantCode: subject.tenantCode },
        });
        if (result.error)
          return result.error.status === 404
            ? { data: null }
            : { error: result.error };
        return { data: result.data as RrcEp10WasteManifestBuildStatus | null };
      },
    }),
    getRrcEp10WasteManifestVersionsBySubject: builder.query<
      RrcEp10Document[],
      ComplianceSubject
    >({
      query: (subject) => ({
        url: `${complianceSubjectPath(subject)}/versions`,
        params: { tenantCode: subject.tenantCode },
      }),
    }),
    getRrcEp10WasteManifestDownload: builder.mutation<
      string,
      { id: string; tenantCode?: string; format: ComplianceExportFormat }
    >({
      query: rrcEp10WasteManifestDownloadRequest,
    }),
    getRrcEp10WasteManifestByPickup: builder.query<
      RrcEp10Document,
      { id: string; tenantCode?: string }
    >({
      query: ({ id, tenantCode }) => ({
        url: `/compliance/rrc/ep10/by-pickup/${encodeURIComponent(id)}`,
        params: { tenantCode },
      }),
      providesTags: (result) =>
        result ? [{ type: 'RrcEp10WasteManifests', id: result.id }] : [],
    }),

    getRrcEp10WasteManifest: builder.query<
      RrcEp10WasteManifest,
      {
        id: string;
        tenantCode?: string;
      }
    >({
      query: ({ id, tenantCode }) => ({
        url: `/compliance/rrc/ep10/${encodeURIComponent(id)}`,
        method: 'GET',
        params: removeUndefined({
          tenantCode: tenantCode,
        }),
      }),
      providesTags: (result, error, { id }) => [
        { type: 'RrcEp10WasteManifests', id },
      ],
    }),
    getRrcEp10WasteManifestByKey: builder.query<
      RrcEp10WasteManifest,
      {
        manifestNumber: string;
        tenantCode?: string;
      }
    >({
      query: ({ manifestNumber, tenantCode }) => ({
        url: `/compliance/rrc/ep10/by-number/${encodeURIComponent(
          manifestNumber
        )}`,
        method: 'GET',
        params: removeUndefined({
          tenantCode: tenantCode,
        }),
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            { type: 'RrcEp10WasteManifests', id: result.id },
            {
              type: 'NumberRrcEp10WasteManifests',
              id: `${arg.manifestNumber}~${arg.tenantCode ?? '(nocode)'}`,
            },
          ];
        } else {
          return [
            {
              type: 'NumberRrcEp10WasteManifests',
              id: `${arg.manifestNumber}~${arg.tenantCode ?? '(nocode)'}`,
            },
          ];
        }
      },
    }),
    getRrcEp10WasteManifestByTta: builder.query<
      RrcEp10WasteManifest,
      {
        id: string;
        tenantCode?: string;
      }
    >({
      query: ({ id, tenantCode }) => ({
        url: `/compliance/rrc/ep10/by-tta/${encodeURIComponent(id)}`,
        method: 'GET',
        params: removeUndefined({
          tenantCode: tenantCode,
        }),
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            { type: 'RrcEp10WasteManifests', id: result.id },
            {
              type: 'TtaRrcEp10WasteManifests',
              id: `${arg.id}~${arg.tenantCode ?? '(nocode)'}`,
            },
          ];
        } else {
          return [];
        }
      },
    }),
    getRrcEp10WasteManifestsByKey: builder.query<
      RrcEp10WasteManifest[],
      {
        manifestNumber: string;
        tenantCode?: string;
      }
    >({
      query: ({ manifestNumber, tenantCode }) => ({
        url: `/compliance/rrc/ep10/by-number/${encodeURIComponent(
          manifestNumber
        )}/versions`,
        method: 'GET',
        params: removeUndefined({
          tenantCode: tenantCode,
        }),
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            ...result.map(
              ({ id }) => ({ type: 'RrcEp10WasteManifests', id } as const)
            ),
            {
              type: 'NumberVersionsRrcEp10WasteManifests',
              id: `${arg.manifestNumber}~${arg.tenantCode ?? '(nocode)'}`,
            },
          ];
        } else {
          return [
            {
              type: 'NumberVersionsRrcEp10WasteManifests',
              id: `${arg.manifestNumber}~${arg.tenantCode ?? '(nocode)'}`,
            },
          ];
        }
      },
    }),
  }),
});

export const {
  useGetRrcEp10WasteManifestQuery,
  useGetRrcEp10WasteManifestByPickupQuery,
  useGetRrcEp10WasteManifestBuildStatusQuery,
  useGetRrcEp10WasteManifestVersionsBySubjectQuery,
  useGetRrcEp10WasteManifestDownloadMutation,
  useGetRrcEp10WasteManifestByKeyQuery,
  useGetRrcEp10WasteManifestByTtaQuery,
  useGetRrcEp10WasteManifestsByKeyQuery,
} = rrcEp10WasteManifestApi;

export const downloadRrcEp10WasteManifest = (
  id: string,
  tenantCode: string | undefined,
  api: BaseQueryApi
): Promise<Response> => {
  const headers = buildStandardHeaders(api);
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  const request: RequestInit = {
    method: 'GET',
    headers: headers,
  };

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;

  const endpoint = rrcEp10WasteManifestDownloadRequest({ id, tenantCode });
  const url = new URL(`${baseUrl}${endpoint.url}`);
  if (tenantCode) url.searchParams.set('tenantCode', tenantCode);

  return window.fetch(url, request).then((response) => {
    if (response.ok) {
      return Promise.resolve(response);
    } else {
      return Promise.reject(response);
    }
  });
};
